import { CurrencyPipe, formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Paginator } from 'primeng/paginator';
import { DemoMerchantPayoutResponse } from 'src/app/_data/merchant-payout-report';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { FilterServiceService } from 'src/app/_services/filter-service.service';
import { GlobalService } from 'src/app/_services/global.service';
import { PayoutReportService } from 'src/app/_services/payout-report.service';
import { environment } from 'src/environments/environment';

/**
 * Merchant-facing "Payout Report" — pulls sales/payout rows from the Order service
 * (order-svc/api/Invoice/GetSalesReport), one row per order. The summary cards are taken
 * from the API's summary object when present, otherwise computed from the returned rows.
 * Export columns are defined locally (colHeaderRow/colFldsRow) rather than via reports[]
 * 'Merchant Payout Report' — that shared entry is for the admin bizz-dashboard equivalent.
 * Keeps a !environment.production demo-data fallback so the page renders without a backend.
 */
@Component({
  selector: 'app-payout-report',
  templateUrl: './payout-report.component.html',
  styleUrls: ['./payout-report.component.scss']
})
export class PayoutReportComponent implements OnInit {

  _globals = GlobalService;

  page = 0;
  rows = 10;
  first = 0;
  totalRecords = 0;
  dataList: any[] = null;
  orderParam = '';
  orderBy = 'asc';
  locationIds: string[] = [];
  isLoading = false;
  skeletonRows: { index: number }[] = [];

  summary: any = {
    totalSales: 0,
    totalOrders: 0,
    totalPaymentGatewayFees: 0,
    totalPlatformFeesCollected: 0,
    totalNetPayout: 0
  };

  /** One row per order — Order#, Customer Name, Total Amount Paid, Payment Gateway Fee, Platform Fee,
   *  Net Payout — always, per the design spec table. colHeader is wrapped in an outer array
   *  (single header row) to match exportExcel/exportPdf's expected shape (`colHeader[0]` for Excel,
   *  `colHeader` itself for the PDF autoTable `head`). */
  private readonly colHeaderRow = ['Order #', 'Customer Name', 'Total Amount Paid', 'Payment Gateway Fee', 'Platform Fee', 'Net Payout'];
  private readonly colFldsRow = ['orderNumber', 'customerName', 'totalAmountPaid', 'paymentGatewayFee', 'platformFee', 'netPayout'];

  get colHeader(): string[][] {
    return [this.colHeaderRow];
  }
  get colflds(): string[] {
    return this.colFldsRow;
  }

  @ViewChild('p', { static: false }) paginator: Paginator;

  constructor(
    public _abd: AppBaseDataService,
    public _filterService: FilterServiceService,
    private currencyPipe: CurrencyPipe,
    private _reportsService: PayoutReportService,
  ) { }

  ngOnInit(): void {
    this._globals.componentName = 'Payout Report';
    this._globals.secondTitle = 'Payout Report';
  }

  ngAfterViewInit() {
    let ti = null;
    ti = setInterval(() => {
      const filter = this._filterService.getFilters(this._globals.componentName);
      if (filter) {
        if (filter.filterValue) this._filterService.searchText = filter.filterValue;
      }
      clearInterval(ti);
      this.getDataList();
    }, 500);
  }

  ngOnDestroy() {
    this._globals.componentName = '';
    this._globals.secondTitle = '';
  }

  /** Build LocationIds from the logged-in user (userLocations), falling back to the
   *  selected-location filter / master locations list. */
  private getLocationsList() {
    const userLocations = GlobalService.userInfo?.userLocations;
    if (Array.isArray(userLocations) && userLocations.length) {
      this.locationIds = userLocations.map((x: any) => String(x));
      return;
    }

    const self = this;
    this.locationIds = [];
    let selLocation: any = this._filterService.selectedLocation;
    if (!selLocation || selLocation.length < 1) {
      selLocation = JSON.parse(JSON.stringify(this._abd.locations || []));
    }
    selLocation = (typeof selLocation != 'undefined' && selLocation instanceof Array) ? selLocation : [selLocation];
    selLocation.forEach((value: any) => {
      if (value?.locationId !== undefined && value?.locationId !== null) {
        self.locationIds.push(value.locationId.toString());
      }
    });
  }

  /** Request payload for Invoice/GetSalesReport. */
  private buildPayload() {
    this.getLocationsList();
    const op = this._filterService.selectedProductSellTypeFilter;
    return {
      StartDate: formatDate(this._filterService.dateRange[0], 'MM/dd/yyyy', 'en-US'),
      EndDate: formatDate(this._filterService.dateRange[1], 'MM/dd/yyyy', 'en-US'),
      LocationIds: this.locationIds,
      OrderPlacement: op?.Value || 'All',
      // exact-match customer drill-in when available, else fuzzy name search
      SearchText: this._filterService.searchText || '',
      OrderParam: this.orderParam,
      OrderBy: this.orderBy,
      PageNo: this.page,
      PageSize: this.rows,
    };
  }

  /** Normalize an invoice/sales row to the payout table's fields (defensive on casing). */
  private mapRow(row: any) {
    return {
      orderNumber: row?.orderNumber ?? row?.OrderNumber ?? row?.invoiceNumber ?? row?.InvoiceNumber ?? '',
      customerId: row?.customerId ?? row?.CustomerId ?? null,
      customerName: row?.customerName ?? row?.CustomerName ?? '',
      orderDate: row?.orderDate ?? row?.OrderDate ?? row?.invoiceDate ?? row?.InvoiceDate ?? null,
      totalAmountPaid: Number(row?.totalAmountPaid ?? row?.TotalAmountPaid ?? row?.grandTotal ?? row?.GrandTotal ?? 0),
      paymentGatewayFee: Number(row?.paymentGatewayFee ?? row?.PaymentGatewayFee ?? 0),
      platformFee: Number(row?.platformFee ?? row?.PlatformFee ?? 0),
      refundAmount: Number(row?.refundAmount ?? row?.RefundAmount ?? row?.refund ?? row?.Refund ?? 0),
      netPayout: Number(row?.netPayout ?? row?.NetPayout ?? 0),
    };
  }

  get displayList(): any[] {
    return this.dataList || [];
  }

  get displayTotalRecords(): number {
    return this.totalRecords;
  }

  private setSkeletonRows(): void {
    const length = this.rows > 0 ? this.rows : 10;
    this.skeletonRows = Array.from({ length }, (_, index) => ({ index }));
  }

  getDataList() {
    this.isLoading = true;
    this.setSkeletonRows();
    this._abd.showSpinner();
    const payload = this.buildPayload();

    this._reportsService.getSalesReport(payload).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this._abd.hideSpinner();
        // Order-svc GetSalesReport nests rows + summary under `data`.
        const data = res?.data ?? res?.Data;
        const rows = data?.rows ?? data?.Rows;
        if (res?.success !== false && Array.isArray(rows)) {
          this.dataList = rows.map((r: any) => this.mapRow(r));
          // Don't trust an API totalRecords/totalCount that comes back 0/missing while rows did
          // come back (`??` doesn't catch a literal 0) — that desyncs the paginator from the table.
          const apiTotal = Number(res?.totalRecords ?? res?.totalCount);
          this.totalRecords = apiTotal > 0 ? apiTotal : this.dataList.length;
          this.applySummary(data?.summary ?? data?.Summary);
        } else {
          this.handleListErrorFallback();
        }
      },
      error: () => {
        this.handleListErrorFallback();
      }
    });

    this._filterService.updateFilters(this._globals.componentName, payload);
    this._filterService.updatePages(this._globals.componentName, { page: this.page, rows: this.rows });
  }

  /** Use the API's summary object when present, otherwise derive it from the rows. */
  private applySummary(apiSummary: any) {
    if (apiSummary) {
      this.summary = {
        totalSales: Number(apiSummary.totalSales ?? apiSummary.TotalSales ?? 0),
        totalOrders: Number(apiSummary.totalOrders ?? apiSummary.TotalOrders ?? 0),
        // API names this `paymentGatewayFee` (no `total` prefix), unlike the other summary fields.
        totalPaymentGatewayFees: Number(apiSummary.totalPaymentGatewayFees ?? apiSummary.TotalPaymentGatewayFees ?? apiSummary.paymentGatewayFee ?? apiSummary.PaymentGatewayFee ?? 0),
        totalPlatformFeesCollected: Number(apiSummary.totalPlatformFeesCollected ?? apiSummary.TotalPlatformFeesCollected ?? 0),
        // API names this `totalNetEarnings` (also exposes `netPayout`); the card label is "Net Earnings / Payout".
        totalNetPayout: Number(apiSummary.totalNetEarnings ?? apiSummary.TotalNetEarnings ?? apiSummary.netPayout ?? apiSummary.NetPayout ?? apiSummary.totalNetPayout ?? 0),
      };
      return;
    }
    this.computeSummary();
  }

  /** Derive the summary cards from the loaded rows (sales, order count, platform fees, net payout). */
  private computeSummary() {
    const rows = this.dataList || [];
    this.summary = {
      totalSales: rows.reduce((s, r) => s + (r.totalAmountPaid || 0), 0),
      totalOrders: rows.length,
      totalPaymentGatewayFees: rows.reduce((s, r) => s + (r.paymentGatewayFee || 0), 0),
      totalPlatformFeesCollected: rows.reduce((s, r) => s + (r.platformFee || 0), 0),
      totalNetPayout: rows.reduce((s, r) => s + (r.netPayout || 0), 0),
    };
  }

  private handleListErrorFallback() {
    this.isLoading = false;
    this._abd.hideSpinner();
    if (!environment.production) {
      // No real backend in dev — filter the mock the same way the search box would.
      const search = (this._filterService.searchText || '').trim().toLowerCase();
      const filtered = (DemoMerchantPayoutResponse.data || []).filter((row: any) => {
        if (search) return String(row.customerName || '').toLowerCase().includes(search);
        return true;
      }).map((r: any) => this.mapRow(r));
      this.dataList = filtered;
      this.totalRecords = filtered.length;
      this.computeSummary();
    } else {
      this.dataList = [];
      this.totalRecords = 0;
      this.computeSummary();
    }
  }

  refrestDataList() {
    this.getDataList();
  }

  callbackFuncFromFilter(value: any) {
    this.getDataList();
  }

  filterButtonPressed() {
    this.getDataList();
  }

  onPageChange(e) {
    this.page = e.page;
    this.rows = e.rows;
    this.first = e.first;
    this.getDataList();
  }

  customSort(e) {
    const last = this.orderParam;
    switch (e.sortField) {
      case 'orderNumber':
        this.orderParam = 'orderNumber';
        break;
      case 'customerName':
        this.orderParam = 'customerName';
        break;
      case 'orderDate':
        this.orderParam = 'orderDate';
        break;
      case 'totalAmountPaid':
        this.orderParam = 'totalAmountPaid';
        break;
      case 'netPayout':
        this.orderParam = 'netPayout';
        break;
      default:
        this.orderParam = '';
    }
    if (this.orderParam !== '') {
      this.orderBy = e.sortOrder == 1 ? 'asc' : 'desc';
      if (last !== this.orderParam) this.orderBy = 'asc';
      this.getDataList();
    }
  }

  calculateSummaryTotal(col: string) {
    if (!this.displayList?.length) return GlobalService.formatNumberWithCurrencySign(0);
    const total = this.displayList.reduce((sum, item) => sum + (item[col] || 0), 0);
    return GlobalService.formatNumberWithCurrencySign(total);
  }

  // Export exactly what's on screen — same rows as `displayList` drives the table with.
  // printsummary: true so the Excel totals row matches the on-screen/PDF Total row.
  exportExcel() {
    this._abd.exportExcel(this.displayList, this.colHeader[0], this.colflds, 'Payout Report', true);
  }

  exportPdf() {
    this._abd.exportPdf(this.displayList, this.colHeader, this.colflds, 'Payout Report', true, false, false);
  }
}
